import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

const PRIMARY = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
  contrastText: '#fff'
};

const TEAL = {
  lighter: '#f3d6ff',
  light: '#d684ff',
  main: '#9c33ff',
  dark: '#6d19b7',
  darker: '#49097a',
  contrastText: '#fff'
};
const GREENISH =  {
  lighter: '#b1e1da',
  light: '#7fbbb2',
  main: '#097a58',
  dark: '#044230',
  darker: '#003626',
  contrastText: GREY[800]

}
const INDIGO = {
  lighter: '#ffd6e2',
  light: '#ff84ab',
  main: '#ff3370',
  dark: '#7a0940',
  darker: '#7a093c',
  contrastText: '#fff'
};
const ORANGY = {
  lighter: '#ffe2d6',
  light: '#ffaf84',
  main: '#ff6333',
  dark: '#7a3109',
  darker: '#7a2d09',
  contrastText: '#fff'
};
const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff'
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800]
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800]
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff'
};

const ERROR_LIGHT = {
  lighter: '#ffe7e8',
  light: '#ffe7e8',
  main: '#ffc3c3',
  dark: '#e88b81',
  darker: '#d56154',
  contrastText: GREY[800]
};
const SUCCESS_LIGHT = {
  lighter: '#f1fff0',
  light: '#dbffda',
  main: '#c3ffc3',
  dark: '#aafeaa',
  darker: '#95fa97',
  contrastText: GREY[800]
};

const WHITE = {
  lighter: '#fafafa',
  light: '#f5f5f5',
  main: '#f0f0f0',
  dark: '#dedede',
  darker: '#c2c2c2',
  contrastText: GREY[800]
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  indigo: {...INDIGO},
  greenish: {...GREENISH},
  teal: {...TEAL},
  info: { ...INFO },
  orangy: {...ORANGY},
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  error_light: {...ERROR_LIGHT},
  success_light: {...SUCCESS_LIGHT},
  white: {...WHITE},
  grey: GREY,
  gradients: GRADIENTS,
  divider: GREY[500_24],
  chart: CHART_COLORS,
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action }
  }
};

export default palette;
